import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { fetchSkyAgentNav } from "@/lib/api-client";
import { getAuthMode, getTokenCookie, SKYAGENT_URL } from "@/lib/auth";
import { qk } from "@/lib/query-keys";

const SkyAgentNavItemSchema = z.object({
  label: z.string(),
  href: z.string(),
  group: z.enum(["ops", "driver", "sales"]),
});

const SkyAgentNavResponseSchema = z.object({
  items: z.array(SkyAgentNavItemSchema),
});

export type SkyAgentNavItem = z.infer<typeof SkyAgentNavItemSchema>;

/**
 * The SkyAgent left-hand nav, resolved for the current user and rendered inside
 * the Performance Dashboard so the SkyAgent menu stays available (NAT-55).
 *
 * Only runs in `skyagent` auth mode with a token present and a configured
 * SkyAgent URL — otherwise there is nothing to render and the dashboard shows
 * full-width. Failures are swallowed by the component (empty menu), so a nav
 * outage never blocks the dashboard.
 */
export function useSkyAgentNav() {
  const token = getTokenCookie() ?? "";
  const enabled = getAuthMode() === "skyagent" && Boolean(SKYAGENT_URL) && Boolean(token);

  return useQuery<SkyAgentNavItem[]>({
    queryKey: qk.skyagentNav(),
    queryFn: async ({ signal }) => {
      const raw = await fetchSkyAgentNav(SKYAGENT_URL, token, signal);
      return SkyAgentNavResponseSchema.parse(raw).items;
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}
